import { lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import { propertySections } from './sections';
import type { PropertySectionDef } from './context';

// Pages and tabs load when first opened (NFR-04).
const PropertyHomePage = lazy(() => import('./pages/PropertyHomePage'));
const PropertySectionPage = lazy(() => import('./pages/PropertySectionPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const PropertyMapPage = lazy(() => import('./pages/PropertyMapPage'));
const AgreementPage = lazy(() => import('./pages/AgreementPage'));
const WorkOrderPage = lazy(() => import('./pages/WorkOrderPage'));
const AssetPage = lazy(() => import('./pages/AssetPage'));
const ReportFaultPage = lazy(() => import('./pages/ReportFaultPage'));

/**
 * The property module's routes, spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages meeting 403s.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Property.View}>
    <RequireModule module={Modules.Property}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: PropertySectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<PropertySectionPage section={def as unknown as PropertySectionDef} />) });

export const propertyRoutes: RouteObject[] = [
  { path: 'property', element: guard(<PropertyHomePage />) },
  section(propertySections.register, 'property/register/:view?'),
  section(propertySections.leasing, 'property/leasing/:view?'),
  section(propertySections.billing, 'property/billing/:view?'),
  section(propertySections.assets, 'property/assets/:view?'),
  section(propertySections.work, 'property/work/:view?'),
  { path: 'property/work/order/:id', element: guard(<WorkOrderPage />) },
  { path: 'property/asset/:id', element: guard(<AssetPage />) },
  section(propertySections.contractors, 'property/contractors/:view?'),
  section(propertySections.compliance, 'property/compliance/:view?'),
  { path: 'property/agreement/:id', element: guard(<AgreementPage />) },
  { path: 'property/map', element: guard(<PropertyMapPage />) },
  section(propertySections.airport, 'property/airport/:view?'),
  { path: 'property/location/:id', element: guard(<LocationPage />) },
  section(propertySections.settings, 'property/settings/:view?'),
];

/** The anonymous page behind an asset's or space's QR code (docs §214); the server shows nothing where the organisation has not opened it. */
export const propertyPublicRoutes: RouteObject[] = [
  { path: '/report-fault/:orgSlug/:locationId', element: <ReportFaultPage /> },
];
